import React from "react";
import { Form } from "react-bootstrap";
import {
  COLUMN_TYPES,
  ORIGIN_SCOPE_LABELS,
  type LayoutCellInput,
  type LayoutMetaTemplate,
} from "@features/presentationLayout/types/presentationLayout";
import { LAYOUT_BUILDER_EDIT_CELLS_TEST_ID } from "@features/presentationLayout/constants/presentationLayoutConstants";
import {
  emptyCell,
  kpiPickerOptions,
  type BindingCatalog,
  type DraftColumn,
  type DraftRow,
} from "@features/presentationLayout/utils/layoutForm";

type Props = {
  row: DraftRow;
  columns: DraftColumn[];
  catalog: BindingCatalog;
  templates: LayoutMetaTemplate[];
  disabled: boolean;
  testIdPrefix: string;
  onChange: (cells: Array<LayoutCellInput | null>) => void;
};

const toId = (value: string): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Edit cells panel: Leave blank or Bind to data for each Value column on a Binding table row. */
const BindingCellsEditor: React.FC<Props> = ({
  row,
  columns,
  catalog,
  templates,
  disabled,
  testIdPrefix,
  onChange,
}) => {
  const patchCell = (columnIndex: number, changes: Partial<LayoutCellInput> | null) => {
    const next = columns.map((column, index) => {
      if (column.column_type === COLUMN_TYPES.CALCULATED) return null;
      if (index !== columnIndex) return row.cells[index] ?? emptyCell();
      if (changes === null) return emptyCell();
      return { ...(row.cells[index] ?? emptyCell()), ...changes };
    });
    onChange(next);
  };

  return (
    <div className="border rounded p-2 bg-light" data-testid={`${testIdPrefix}-${LAYOUT_BUILDER_EDIT_CELLS_TEST_ID}`}>
      <div className="fw-semibold small mb-2">Edit cells</div>
      {columns.map((column, columnIndex) => {
        if (column.column_type !== COLUMN_TYPES.VALUE) {
          return (
            <div key={column.key} className="text-muted small mb-2">
              {column.label.trim() || `Column ${columnIndex + 1}`}: calculated (no cell binding)
            </div>
          );
        }

        const cell = row.cells[columnIndex] ?? emptyCell();
        const activities = cell.report_template_id !== null
          ? catalog.template(cell.report_template_id)?.activities ?? []
          : [];
        const kpiOptions = cell.report_template_id !== null && cell.activity_id !== null
          ? kpiPickerOptions(catalog.activity(cell.report_template_id, cell.activity_id)?.kpis ?? [], cell.kpi_id)
          : [];
        const columnLabel = column.label.trim() || `Column ${columnIndex + 1}`;
        const mode = cell.is_empty ? "blank" : "bind";

        return (
          <div key={column.key} className="mb-3 pb-2 border-bottom" data-testid={`${testIdPrefix}-cell-${columnIndex}`}>
            <div className="fw-semibold small mb-1">{columnLabel}</div>
            <Form.Check
              type="radio"
              id={`${row.key}-col-${columnIndex}-blank`}
              name={`${row.key}-col-${columnIndex}-mode`}
              label="Leave blank"
              checked={mode === "blank"}
              disabled={disabled}
              className="small"
              onChange={() => patchCell(columnIndex, {
                is_empty: true,
                report_template_id: null,
                activity_id: null,
                kpi_id: null,
              })}
            />
            <Form.Check
              type="radio"
              id={`${row.key}-col-${columnIndex}-bind`}
              name={`${row.key}-col-${columnIndex}-mode`}
              label="Bind to data"
              checked={mode === "bind"}
              disabled={disabled}
              className="small mb-2"
              onChange={() => patchCell(columnIndex, {
                is_empty: false,
                report_template_id: null,
                activity_id: null,
                kpi_id: null,
              })}
            />
            {mode === "bind" ? (
              <div className="d-flex flex-column gap-1 ps-3">
                <Form.Select
                  size="sm"
                  value={cell.report_template_id ?? ""}
                  disabled={disabled}
                  aria-label={`${columnLabel} template`}
                  onChange={(e) => patchCell(columnIndex, {
                    is_empty: false,
                    report_template_id: toId(e.target.value),
                    activity_id: null,
                    kpi_id: null,
                  })}
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template.report_template_id} value={template.report_template_id}>
                      {template.report_template_name} ({ORIGIN_SCOPE_LABELS[template.origin_scope]})
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={cell.activity_id ?? ""}
                  disabled={disabled || activities.length === 0}
                  aria-label={`${columnLabel} activity`}
                  onChange={(e) => patchCell(columnIndex, {
                    is_empty: false,
                    activity_id: toId(e.target.value),
                    kpi_id: null,
                  })}
                >
                  <option value="">Select an activity...</option>
                  {activities.map((activity) => (
                    <option key={activity.activity_id} value={activity.activity_id}>{activity.activity_name}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  value={cell.kpi_id ?? ""}
                  disabled={disabled || kpiOptions.length === 0}
                  aria-label={`${columnLabel} KPI`}
                  onChange={(e) => patchCell(columnIndex, {
                    is_empty: false,
                    kpi_id: toId(e.target.value),
                  })}
                >
                  <option value="">Select a KPI...</option>
                  {kpiOptions.map(({ kpi, disabled: unavailable }) => (
                    <option key={kpi.kpi_id} value={kpi.kpi_id} disabled={unavailable}>{kpi.kpi_name}</option>
                  ))}
                </Form.Select>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default BindingCellsEditor;
